import React, { useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";

import "./form.css";

// ✅ Initialize Supabase client
const supabase = createClient(
	process.env.REACT_APP_SUPABASE_URL,
	process.env.REACT_APP_SUPABASE_ANON_KEY
);

export default function CourseEnrollment() {
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		paymentMode: "",
		selectedCourses: [],
	});

	const [total, setTotal] = useState(0);
	const [couponCode, setCouponCode] = useState("");
	const [discountApplied, setDiscountApplied] = useState(false);
	const [couponMsg, setCouponMsg] = useState("");

	const courses = [
		{ name: "HTML & CSS", price: 3000 },
		{ name: "JavaScript", price: 3000 },
		{ name: "UI/UX", price: 3000 },
	];

	// Update selected courses
	const handleCourseChange = (courseName, price) => {
		setFormData((prev) => {
			const alreadySelected = prev.selectedCourses.includes(courseName);
			const updatedCourses = alreadySelected
				? prev.selectedCourses.filter((c) => c !== courseName)
				: [...prev.selectedCourses, courseName];

			const newTotal = updatedCourses.length * 3000;
			const discountedTotal =
				discountApplied && couponCode === "Simply500"
					? newTotal - 500 * updatedCourses.length
					: newTotal;

			setTotal(Math.max(discountedTotal, 0));

			return { ...prev, selectedCourses: updatedCourses };
		});
	};

	const handleChange = (e) => {
		const { id, value } = e.target;
		setFormData((prev) => ({ ...prev, [id]: value }));
	};

	const handlePaymentChange = (e) => {
		setFormData((prev) => ({ ...prev, paymentMode: e.target.value }));
	};

	const handleCouponApply = () => {
		if (discountApplied) return;

		// Check if coupon is expired (after Feb 1, 2026)
		const currentDate = new Date();
		const expirationDate = new Date("2026-02-01T00:00:00");
		const isCouponExpired = currentDate > expirationDate;

		if (couponCode === "Simply500") {
			if (isCouponExpired) {
				setCouponMsg("⏰ Coupon Code Expired");
				return;
			}

			if (formData.selectedCourses.length > 0) {
				const discountAmount = 500 * formData.selectedCourses.length;
				setTotal((prev) => Math.max(prev - discountAmount, 0));
				setDiscountApplied(true);
				setCouponMsg("🎉 Coupon Applied Successfully!");
				launchConfetti();
			} else {
				setCouponMsg("❌ Please select at least one course");
			}
		} else {
			setCouponMsg("❌ Invalid Coupon Code");
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (formData.selectedCourses.length === 0) {
			alert("Please select at least one course!");
			return;
		}

		const finalAmount = total;

		const enrollmentData = {
			name: formData.name,
			email: formData.email,
			selected_courses: formData.selectedCourses,
			total_amount: finalAmount,
			coupon_code: couponCode || null,
			discount_applied: discountApplied,
			payment_mode: formData.paymentMode,
			payment_status: "pending",
		};

		const { error } = await supabase
			.from("enrollments")
			.insert([enrollmentData]);

		if (error) {
			console.error(error);
			alert("⚠️ Something went wrong. Try again!");
			return;
		}

		// Navigate to payment page for UPI
		if (formData.paymentMode === "upi") {
			navigate("/payment", {
				state: {
					amount: finalAmount,
					enrollmentData: enrollmentData,
				},
			});
		} else {
			alert("✅ Enrollment saved! Please pay in cash during your first class.");
		}
	};

	// Confetti animation
	const launchConfetti = () => {
		for (let i = 0; i < 20; i++) {
			const confetti = document.createElement("div");
			confetti.classList.add("confetti");
			confetti.style.left = `${Math.random() * 100}%`;
			confetti.style.backgroundColor = ["#6699CC", "#141e26", "#00bfa5"][
				Math.floor(Math.random() * 3)
			];
			document.body.appendChild(confetti);
			setTimeout(() => confetti.remove(), 3000);
		}
	};

	//PAYMENT
	const handleUpiPayment = (amount) => {
		const upiBaseUrl = "upi://pay";
		const params = new URLSearchParams({
			pa: "9359118747@ptsbi", // your UPI ID
			pn: "Soham Wani",
			am: amount,
			cu: "INR",
			tn: "Payment for Service",
		});

		const upiUrl = `${upiBaseUrl}?${params.toString()}`;

		const isMobile =
			/Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
				navigator.userAgent
			);

		if (isMobile) {
			// On mobile → directly open UPI app
			window.location.href = upiUrl;
		} else {
			// On desktop → open QR code in new tab
			const qrUrl =
				"https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=" +
				encodeURIComponent(upiUrl);
			window.open(qrUrl, "_blank");
		}
	};

	return (
		<div className="form-container" id="enroll">
			<div className="logo-wrapper">
				<img src="/logo.svg" alt="Brand Logo" />
			</div>

			<h2 className="form-title">Course Enrollment</h2>

			<form id="enrollmentForm" onSubmit={handleSubmit}>
				{/* Name + Email */}
				<div className="form-group double">
					<div className="input-wrapper">
						<label className="label" htmlFor="name">
							Full Name
						</label>
						<input
							type="text"
							id="name"
							name="name"
							className="input-field"
							placeholder="Enter your name"
							value={formData.name}
							onChange={handleChange}
							required
						/>
					</div>

					<div className="input-wrapper">
						<label className="label" htmlFor="email">
							Email
						</label>
						<input
							type="email"
							id="email"
							name="email"
							className="input-field"
							placeholder="Enter your email"
							value={formData.email}
							onChange={handleChange}
							required
						/>
					</div>
				</div>

				{/* Course Selection */}
				<div className="form-group course-sel">
					<label className="label">Select Courses</label>
					<div className="checkbox-group">
						{courses.map((course) => (
							<label key={course.name} className="checkbox-label">
								<input
									type="checkbox"
									className="checkbox"
									checked={formData.selectedCourses.includes(course.name)}
									onChange={() => handleCourseChange(course.name, course.price)}
								/>
								{course.name} — ₹{course.price}
							</label>
						))}
					</div>
				</div>

				{/* Payment Mode */}
				<div className="form-group">
					<label className="label">Mode of Payment</label>
					<div className="radio-group">
						<label className="radio-label">
							<input
								type="radio"
								className="radio"
								name="payment"
								value="cash"
								checked={formData.paymentMode === "cash"}
								onChange={handlePaymentChange}
								required
							/>
							Cash
						</label>
						<label className="radio-label">
							<input
								type="radio"
								className="radio"
								name="payment"
								value="upi"
								checked={formData.paymentMode === "upi"}
								onChange={handlePaymentChange}
								required
							/>
							UPI
						</label>
					</div>
				</div>

				{/* Total + Checkout */}
				<div className="checkout-section">
					<p className="total-text">
						Total Amount Payable: <span id="amount">₹{total}</span>
					</p>
					<button type="submit" className="form__btn checkout-btn">
						Checkout
					</button>
				</div>

				{/* Coupon Section */}
				<div className="coupon-section">
					<input
						type="text"
						id="couponCode"
						className="input-field coupon-input"
						placeholder="Enter Coupon Code"
						value={couponCode}
						onChange={(e) => setCouponCode(e.target.value)}
						disabled={discountApplied}
					/>
					<button
						type="button"
						id="applyCoupon"
						className={`form__btn coupon-btn ${
							discountApplied ? "applied" : ""
						}`}
						onClick={handleCouponApply}
					>
						{discountApplied ? "Applied!" : "Apply"}
					</button>
				</div>

				<p
					id="couponMessage"
					className={`coupon-message ${
						couponMsg.includes("🎉") ? "success" : "error"
					}`}
				>
					{couponMsg}
				</p>
			</form>
		</div>
	);
}
